import {
  authBridgeEvent,
  HostAuthMessageSchema,
  type HostAuthRequest,
  type HostAuthResponse,
} from "@froppio/host-auth";
import type { AuthChangeListener, AuthSession, IAuthProvider } from "../types/authTypes";

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(message: string): void };
  }
}

type Request = { operation: "getSession" | "logout" }
  | { operation: "getAccessToken"; userId: string };

export function createHostAuthProvider(): IAuthProvider {
  const listeners = new Set<AuthChangeListener>();
  const pending = new Map<string, {
    resolve: (response: HostAuthResponse) => void;
    reject: (error: Error) => void;
    timeout: number;
  }>();
  let session: AuthSession | null = null;
  let revision = -1;
  let generation = 0;
  let initialized = false;

  const detachIfUnused = () => {
    if (!pending.size && !listeners.size) window.removeEventListener(authBridgeEvent, receive);
  };

  const applySnapshot = (snapshot: Pick<HostAuthResponse, "session" | "revision">) => {
    if (snapshot.revision < revision) return;
    if (snapshot.session?.user.id !== session?.user.id) generation++;
    const changed = snapshot.revision !== revision;
    session = snapshot.session;
    revision = snapshot.revision;
    if (changed) listeners.forEach(listener => listener("HOST_AUTH_CHANGED", session));
  };

  function receive(event: Event) {
    if (!(event instanceof CustomEvent)) return;
    const parsed = HostAuthMessageSchema.safeParse(event.detail);
    if (!parsed.success) {
      const error = new Error("The native app sent an unsupported authentication message. Update or restart Froppio.");
      pending.forEach(entry => {
        window.clearTimeout(entry.timeout);
        entry.reject(error);
      });
      pending.clear();
      console.error(error.message);
      detachIfUnused();
      return;
    }
    const message = parsed.data;
    if (message.type === "changed") {
      // Only a matching response can complete the initial, versioned handshake.
      if (initialized) applySnapshot(message);
      return;
    }
    const entry = pending.get(message.id);
    if (!entry) return;
    window.clearTimeout(entry.timeout);
    pending.delete(message.id);
    if (message.type === "error") {
      entry.reject(new Error(message.message));
    } else if (message.revision < revision) {
      entry.reject(new Error("Your session changed. Please try again."));
    } else {
      initialized = true;
      applySnapshot(message);
      entry.resolve(message);
    }
    detachIfUnused();
  }

  const request = (operation: Request): Promise<HostAuthResponse> => {
    const bridge = window.ReactNativeWebView;
    if (!bridge) return Promise.reject(new Error("Native authentication is unavailable. Open this page in the Froppio app."));
    const id = crypto.randomUUID();
    const message: HostAuthRequest = { channel: authBridgeEvent, version: 1, id, ...operation };
    return new Promise((resolve, reject) => {
      window.addEventListener(authBridgeEvent, receive);
      const timeout = window.setTimeout(() => {
        pending.delete(id);
        reject(new Error("The Froppio app did not respond. Please try again."));
        detachIfUnused();
      }, 10_000);
      pending.set(id, { resolve, reject, timeout });
      try {
        bridge.postMessage(JSON.stringify(message));
      } catch {
        window.clearTimeout(timeout);
        pending.delete(id);
        reject(new Error("Unable to contact the Froppio app. Please try again."));
        detachIfUnused();
      }
    });
  };

  const nativeScreenOnly = async (): Promise<never> => {
    throw new Error("Use the native Froppio screen to sign in or create an account.");
  };

  return {
    async getSession() {
      return (await request({ operation: "getSession" })).session;
    },
    async getAccessToken() {
      if (!initialized) await request({ operation: "getSession" });
      if (!session) return null;
      const userId = session.user.id;
      const currentGeneration = generation;
      const response = await request({ operation: "getAccessToken", userId });
      if (generation !== currentGeneration || response.session?.user.id !== userId) {
        throw new Error("Your session changed. Please try again.");
      }
      if (!response.accessToken) throw new Error("The Froppio app did not provide an access token.");
      return response.accessToken;
    },
    async logout() {
      const response = await request({ operation: "logout" });
      if (response.session) throw new Error("The Froppio app could not finish signing out. Please try again.");
    },
    login: nativeScreenOnly,
    signup: nativeScreenOnly,
    resendSignupConfirmation: nativeScreenOnly,
    requestPasswordReset: nativeScreenOnly,
    updatePassword: nativeScreenOnly,
    onAuthStateChange(callback) {
      listeners.add(callback);
      window.addEventListener(authBridgeEvent, receive);
      return () => {
        listeners.delete(callback);
        detachIfUnused();
      };
    },
  };
}
